import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DestroyableComponent } from '../destroyable.component';
import { catchError, debounceTime, lastValueFrom, Observable, of, takeUntil } from 'rxjs';
import { HereLookupResult, HereSearchResult, HereService } from '../../services/here.service';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { shareReplayComponentConfig } from '../../constants';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'td-address-search-v2',
  templateUrl: './address-search-v2.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressSearchV2Component),
    },
  ],
  standalone: false,
})
export class AddressSearchV2Component extends DestroyableComponent implements ControlValueAccessor, OnChanges {
  @HostBinding('class.contents') public contents = true;

  @Input() public outputType: 'xy' | 'longlat' | 'address' = 'longlat';
  @Input() public label = 'Address Search';
  @Input() public initialSearch: string = null;

  @Output() public addressResult = new EventEmitter<HereLookupResult>();
  public addressSearch = new FormControl<string>(null, []);
  public addresses$: Observable<HereSearchResult[]>;

  private onChange: Function;
  public onTouched: Function;

  constructor(private here: HereService) {
    super();

    this.addresses$ = this.addressSearch.valueChanges.pipe(
      debounceTime(300),
      switchMap((v) => {
        if (!v || v?.length < 3) {
          return of([]);
        }
        return this.here.searchForAddresses$(v);
      }),
      shareReplay(shareReplayComponentConfig),
    );
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.addressSearch.disable();
    } else {
      this.addressSearch.enable();
    }
  }

  public async lookupAndWrite(selected: MatAutocompleteSelectedEvent) {
    const lookup = await lastValueFrom(this.here.lookup$(selected.option.value.id));
    this.writeValue(lookup);
    this.addressResult.next(lookup);
  }

  public writeValue(obj: HereLookupResult): void {
    if (obj) {
      this.addressSearch.setValue(obj?.address?.label || this.initialSearch);
      if (this.onChange && obj.position) {
        switch (this.outputType) {
          case 'address':
            this.onChange(obj);
            break;
          case 'xy':
            this.onChange({
              x: obj.position.lng,
              y: obj.position.lat,
              valid: true,
            });
            break;
          case 'longlat':
            this.onChange({
              longitude: obj.position.lng,
              latitude: obj.position.lat,
            });
        }
      }
    } else {
      if (this.onChange) {
        this.onChange(null);
      }
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.initialSearch && this.addressSearch.pristine) {
      this.addressSearch.setValue(this.initialSearch);
    }
  }
}
